import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { useRole } from "components/Hooks/UserHooks";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DatePicker, { registerLocale } from "react-datepicker";
import { formatDateStringToKorea, formatISOString } from "helpers/format";
import { LIST_OPTION_GENDER, LIST_OPTION_SNS_TYPE } from "helpers/constans";
import { putUser } from "store/thunks";
import UploadFileBK from "components/Common/UploadFileBK";

registerLocale("en", en);
registerLocale("ko", ko);

const ProfileInfoTab = ({data, handleUpdateSuccess, setLinkImage} : any) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);


  const handleSubmit = async (values: any) => {
    if(!data?.id) return;

    try {
      setIsLoading((_prev) => true);
      const formatValues = {
        imageUrls: values?.imageUrls,
        nickname: values?.nickname || "",
        fullname: values?.fullname || "",
        gender: values?.gender || "",
        phoneNumber: values?.phoneNumber || "",
        email: values?.email || null,
        socials: (values?.snsType || values?.snsLink) ? [
          {
            "sns": values?.snsType,
            "url": values?.snsLink || ""
          }
        ] : null,
        dateOfBirth: values?.dateOfBirth
          ? formatISOString(values?.dateOfBirth)
          : null,
      };
      
      const response: any = await putUser(data?.id, {
        profile: formatValues
      });
      
      // console.log(response)
      if (response?.code) {
        setIsLoading((_prev) => false);
        toast(
            `${t("The process has been completed.")}`,
            CONFIG_OPTION_TOAST_NORMAL
        );
        handleUpdateSuccess && handleUpdateSuccess();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    imageUrls: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().nullable(),
        }).required(`${t("This field is required")}`),
      ).required(`${t("This field is required")}`).max(5, "You can only select up to 5 images"),
    nickname: Yup.string().required(`${t("This field is required")}`),
    // email: Yup.string().required("This field is required"),
    email: Yup.string().nullable()
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
        t("Invalid email address")
      ),
    fullname: Yup.string().required(`${t("This field is required")}`),
    gender: Yup.string().required(`${t("This field is required")}`),
    phoneNumber: Yup.string().required(`${t("This field is required")}`)
      .matches(/^\d{1,11}$/, t("Phone number must be up to 11 digits")),
    snsType: Yup.string().nullable(),
    // snsLink: Yup.string().when("snsType", {
    //   is: (value: any) => !!value,
    //   then: (rule) => {
    //     return rule.test(
    //       "snsType",
    //       `${t("This field is required")}`,
    //       (target) => !!target?.trim()
    //     );
    //   },
    // }),
    dateOfBirth: Yup.string().required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      imageUrls: null,
      nickname: "",
      fullname: "",
      gender: "male",
      dateOfBirth: new Date() || "",
      phoneNumber: "",
      email: "",
      snsType: "",
      snsLink: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if(data) {
      const imageUrls = data?.profile?.imageUrls;
      const formatAvatars = imageUrls?.map((item: string) => ({url: item }));
      // console.log(formatAvatars);

      formik.setValues({
        ...data,
        email: data?.profile?.email,
        nickname: data?.profile?.nickname,
        fullname: data?.profile?.fullname,
        gender: data?.profile?.gender,
        phoneNumber: data?.profile?.phoneNumber,
        snsType: data?.profile?.socials?.[0]?.sns,
        snsLink: data?.profile?.socials?.[0]?.url,
        imageUrls: formatAvatars || null,
        dateOfBirth: data?.profile?.dateOfBirth ? new Date(formatDateStringToKorea(data?.profile?.dateOfBirth)) : null,
      })
    }
  }, [data]);
  
  useEffect(() => {
    document.title = `${t("Profile Info")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);
  
  return (
    <React.Fragment>
      <Row>
        <Col lg={12} className="mt-4 px-4">
          <form onSubmit={formik.handleSubmit}>
            <div
              className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
              style={{
                border: "1px dashed var(--vz-primary)",
                borderRadius: "4px",
              }}
            >
              <span
                className="text-primary position-absolute fs-13 px-1"
                style={{
                  backgroundColor: "var(--vz-secondary-bg)",
                  top: "0px",
                  transform: "translateY(-50%)",
                }}
              >
                {t("Info")}
              </span>
              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div>
                  <div className="mb-3">
                    <Label htmlFor="contact_name" className="form-label">
                      {t("Photos")} <span className="text-danger">*</span>
                    </Label>
                    <UploadFileBK
                      formik={formik}
                      fieldValue="imageUrls"
                      isMultiple={true}
                      isUploadMore={
                        (formik?.values?.imageUrls && (formik?.values?.imageUrls as any)?.length >= 5 ? false : true) || (!formik?.values?.imageUrls)
                      }
                      setLinkImage={setLinkImage}
                    />
                    {formik.touched.imageUrls && formik.errors.imageUrls ? (
                      <div className="text-danger mt-2">
                        {formik.errors.imageUrls}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
                <div className="flex-grow-1">
                  <Row>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="nickname" className="form-label">
                          {t("Nickname")} <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="nickname"
                          type="text"
                          className="form-control"
                          id="nickname"
                          placeholder={t("Nickname")}
                          value={formik?.values?.nickname}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "nickname",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.nickname && formik.errors.nickname ? (
                          <div className="text-danger mt-2">
                            {formik.errors.nickname}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="fullname" className="form-label">
                          {t("Full Name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="fullname"
                          type="text"
                          className="form-control"
                          id="fullname"
                          placeholder={t("Full Name")}
                          value={formik?.values?.fullname}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "fullname",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.fullname && formik.errors.fullname ? (
                          <div className="text-danger mt-2">
                            {formik.errors.fullname}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="gender" className="form-label">
                          {t("Gender")} <span className="text-danger">*</span>
                        </Label>
                        <DropdownOptionNew
                          name="gender"
                          dataList={LIST_OPTION_GENDER}
                          placeholder={`${t("Gender")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.gender}
                          onChangeSelect={(e: any) =>
                            formik.setFieldValue("gender", e?.value)
                          }
                          isHasOptionAll={false}
                        />
                        {formik.touched.gender && formik.errors.gender ? (
                          <div className="text-danger mt-2">
                            {formik.errors.gender}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4} className="datepicker-time">
                      <div className="mb-3">
                        <Label htmlFor="dateOfBirth" className="form-label">
                          {t("Birthdate")}{" "}
                          <span className="text-danger">*</span>
                        </Label>

                        <div className="w-100">
                          <DatePicker
                            name={"dateOfBirth"}
                            className="form-control"
                            selected={formik?.values?.dateOfBirth}
                            placeholderText={t("Birthdate")}
                            onChange={(date: any) => {
                              formik.setFieldValue("dateOfBirth", date);
                            }}
                            dateFormat="yyyy-MM-dd"
                            isClearable={true}
                          />
                        </div>

                        {formik.touched.dateOfBirth && formik.errors.dateOfBirth ? (
                          <div className="text-danger mt-2">
                            {String(formik.errors.dateOfBirth)}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="phoneNumber" className="form-label">
                          {t("Phone Number")} {" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="phoneNumber"
                          type="number"
                          className="form-control"
                          id="phoneNumber"
                          placeholder={t("Phone Number")}
                          value={formik?.values?.phoneNumber}
                          onKeyDown={(e) => {
                            if (['e', 'E', '+', '-'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(event: any) => {
                            const inputValue = event.target.value;
                            if (inputValue !== undefined && inputValue.length <= 11 && /^\d*$/.test(inputValue)) {
                              formik.setFieldValue(
                                "phoneNumber",
                                inputValue
                              )
                            }
                          }}
                        />
                        {formik.touched.phoneNumber &&
                        formik.errors.phoneNumber ? (
                          <div className="text-danger mt-2">
                            {formik.errors.phoneNumber}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="email" className="form-label">
                          {t("Email")}
                        </Label>
                        <Input
                          name="email"
                          type="text"
                          className="form-control"
                          id="email"
                          placeholder={t("Email")}
                          value={formik?.values?.email}
                          onChange={(event: any) =>
                            formik.setFieldValue("email", event?.target?.value)
                          }
                        />
                        {formik.touched.email && formik.errors.email ? (
                          <div className="text-danger mt-2">
                            {formik.errors.email}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="snsType" className="form-label">
                          {t("SNS Type")}
                        </Label>
                        <DropdownOptionNew
                          name="snsType"
                          dataList={LIST_OPTION_SNS_TYPE || []}
                          placeholder={`${t("SNS Type")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik?.values?.snsType}
                          onChangeSelect={(e: any) => {
                            if(!e?.value) {
                              formik.setFieldValue("snsLink", "")
                            }
                            formik.setFieldValue("snsType", e?.value)
                          }}
                          isHasOptionAll={false}
                          lstImage={true}
                          isClearable={true}
                        />
                        {formik.touched.snsType && formik.errors.snsType ? (
                          <div className="text-danger mt-2">
                            {formik.errors.snsType}
                          </div>
                        ) : null}
                      </div>
                      {formik.touched.snsType && formik.errors.snsType ? (
                        <div className="text-danger mt-2">
                          {formik.errors.snsType}
                        </div>
                      ) : null}
                    </Col>
                    <Col md={4} lg={4}>
                      <div className="mb-3">
                        <Label htmlFor="snsLink" className="form-label">
                          {t("SNS Link")}
                        </Label>
                        <Input
                          name="snsLink"
                          type="text"
                          className="form-control"
                          id="snsLink"
                          placeholder={t("SNS Link")}
                          value={formik?.values?.snsLink}
                          disabled={!formik?.values?.snsType}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "snsLink",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.snsLink && formik.errors.snsLink ? (
                          <div className="text-danger mt-2">
                            {formik.errors.snsLink}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              <Row className="mb-3 mt-2">
                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end">
                    {isHavePermissionRole(
                      ROLES_FOR_APP.USER_UPDATE,
                      userPermissions
                    ) && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading && (
                          <Spinner size="sm me-2"></Spinner>
                        )}
                        {t("Button Save")}
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default ProfileInfoTab;
